import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAT_LABEL = {
  focus: "Focus",
  resilience: "Resilience",
  confidence: "Confidence",
  motivation: "Motivation",
  anxiety: "Anxiety Management",
};

export default function Quiz() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const sessionId = sessionStorage.getItem("mindedge_session");

  useEffect(() => {
    if (!sessionId) {
      navigate("/intake");
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/assessment/questions`);
        setQuestions(data.questions);
      } catch (e) {
        toast.error("Could not load questions.");
      }
    })();
  }, [sessionId, navigate]);

  if (!questions.length) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F7F5]">
        <Loader2 className="w-6 h-6 animate-spin text-[#1C3F39]" />
      </div>
    );
  }

  const q = questions[idx];
  const total = questions.length;
  const progress = ((idx + 1) / total) * 100;
  const isLast = idx === total - 1;
  const selected = answers[q.id];

  const select = (score) => setAnswers({ ...answers, [q.id]: score });

  const next = async () => {
    if (selected == null) {
      toast.error("Pick an answer to continue.");
      return;
    }
    if (!isLast) {
      setIdx(idx + 1);
      return;
    }
    // Submit
    setSubmitting(true);
    try {
      const payload = {
        session_id: sessionId,
        answers: questions.map((qq) => ({
          question_id: qq.id,
          score: answers[qq.id],
        })),
      };
      const { data } = await axios.post(`${API}/assessment/submit`, payload);
      sessionStorage.setItem("mindedge_result", JSON.stringify(data));
      navigate(`/results/${data.id}`);
    } catch (e) {
      toast.error("Could not submit. Please try again.");
      setSubmitting(false);
    }
  };

  const back = () => {
    if (idx > 0) setIdx(idx - 1);
  };

  return (
    <div className="min-h-screen bg-[#F7F7F5] text-[#111827] flex flex-col">
      {/* Progress bar */}
      <div className="w-full px-6 lg:px-12 pt-6">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-3">
            <span
              className="text-xs uppercase tracking-[0.22em] font-bold text-[#1C3F39]"
              data-testid="quiz-category"
            >
              {CAT_LABEL[q.category]}
            </span>
            <span
              className="text-xs uppercase tracking-[0.22em] font-bold text-[#4B5563]"
              data-testid="quiz-counter"
            >
              {String(idx + 1).padStart(2, "0")} / {total}
            </span>
          </div>
          <Progress
            value={progress}
            className="h-1.5 bg-[#E5E7EB] [&>div]:bg-[#E25F38]"
            data-testid="quiz-progress"
          />
        </div>
      </div>

      <section className="flex-1 flex items-start justify-center px-6 lg:px-12 py-12">
        <AnimatePresence mode="wait">
          <motion.div
            key={q.id}
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.25 }}
            className="w-full max-w-2xl"
          >
            <h2
              className="font-display text-3xl md:text-4xl leading-tight mb-10"
              data-testid="quiz-question"
            >
              {q.text}
            </h2>

            <div className="space-y-3" data-testid="quiz-options">
              {q.options.map((opt, i) => {
                const isSelected = selected === opt.score;
                const optKey = `${q.id}-${opt.score}-${opt.label.slice(0, 20)}`;
                return (
                  <button
                    key={optKey}
                    type="button"
                    onClick={() => select(opt.score)}
                    data-testid={`quiz-option-${i}`}
                    className={[
                      "w-full text-left bg-white rounded-xl px-6 py-5 transition-all duration-200",
                      "hover:-translate-y-0.5 hover:shadow-sm flex items-center gap-4",
                      isSelected
                        ? "border-2 border-[#1C3F39] font-bold"
                        : "border border-[#E5E7EB] font-medium hover:border-[#9CAE96]",
                    ].join(" ")}
                  >
                    <span
                      className={[
                        "w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-bold flex-shrink-0",
                        isSelected
                          ? "border-[#1C3F39] bg-[#1C3F39] text-white"
                          : "border-[#E5E7EB] text-[#4B5563]",
                      ].join(" ")}
                    >
                      {String.fromCharCode(65 + i)}
                    </span>
                    <span className="text-base">{opt.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="flex items-center justify-between mt-10">
              <Button
                variant="ghost"
                onClick={back}
                disabled={idx === 0}
                className="text-[#4B5563] hover:text-[#1C3F39] hover:bg-transparent"
                data-testid="quiz-back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={2.5} />
                Back
              </Button>

              <Button
                onClick={next}
                disabled={submitting}
                className="bg-[#1C3F39] hover:bg-[#132A26] text-white rounded-full px-8 h-12 font-semibold"
                data-testid="quiz-next-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : isLast ? (
                  <>
                    See My Results
                    <ArrowRight className="ml-2 w-5 h-5" strokeWidth={2.5} />
                  </>
                ) : (
                  <>
                    Next
                    <ArrowRight className="ml-2 w-5 h-5" strokeWidth={2.5} />
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        </AnimatePresence>
      </section>
    </div>
  );
}
